import { CampaignStateEnum } from '@/apis/generated/dialbunny';
import type { Campaign } from '@/apis/generated/dialbunny';
import type { CallCenter } from '@/apis/generated/dialpad-contact';
import { format, parse } from 'date-fns';

export function getCallCenterIdFromCampaign(
  campaign: Campaign,
): CallCenter['shortKey'] {
  if (campaign.groupId) {
    return `CallCenter:${campaign.groupId}`;
  } else if (campaign.targetId) {
    const groupId = campaign.targetId.split(':')[0];
    return `CallCenter:${groupId}`;
  } else if (campaign.targetKey) {
    const groupId = campaign.targetKey.split(':')[1];
    return `CallCenter:${groupId}`;
  }
}

export function getFormattedDate(value: number): string {
  return format(new Date(value), "MM/dd/yy 'at' h:mm aaa");
}

// eslint-disable-next-line complexity
export function getFormattedDateByCampaignState(campaign: Campaign): string {
  const state = campaign.state;
  switch (state) {
    case CampaignStateEnum.Active:
      return campaign.dateStarted ? getFormattedDate(campaign.dateStarted) : '';
    case CampaignStateEnum.Paused:
      return campaign.datePaused ? getFormattedDate(campaign.datePaused) : '';
    case CampaignStateEnum.Scheduled:
      return campaign.dateScheduledStart
        ? getFormattedDate(campaign.dateScheduledStart)
        : '';
    case CampaignStateEnum.Complete:
      return campaign.dateEnded ? getFormattedDate(campaign.dateEnded) : '';
    case CampaignStateEnum.Deleted:
      return campaign.dateModified
        ? getFormattedDate(campaign.dateModified)
        : '';
    default:
      return '';
  }
}

export function getFormattedTimezoneDetail(): string {
  const offset = format(new Date(), 'OOOO');
  const timeZone = Intl.DateTimeFormat().resolvedOptions().timeZone;
  return `(${offset}) ${timeZone}`;
}

/**
 * Formats a time string (HH:mm format) using the localized TIME_FORMAT.
 * Handles the special case of '24:00' which represents midnight of the next day.
 *
 * @param time - Time string in HH:mm format (e.g., '08:30', '24:00')
 * @param timeFormat - The format string to use (e.g., 'hh:mm aaa')
 * @returns Formatted time string according to the specified format
 */
export function formatTime(time: string, timeFormat: string): string {
  if (time === '24:00') {
    const baseDate = new Date();
    baseDate.setHours(0, 0, 0, 0);
    return format(baseDate, timeFormat);
  }

  const baseDate = new Date();
  baseDate.setHours(0, 0, 0, 0);

  const parsedTime = parse(time, 'HH:mm', baseDate);
  return format(parsedTime, timeFormat);
}
